import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps,
  IconButton,
  Typography,
  Box,
  styled,
  useTheme,
  Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GlassCard from "./GlassCard";


/**
 * BaseModalProps
 * @param open - Whether the modal is open
 * @param onClose - Function to close the modal
 * @param title - Title of the modal
 * @param children - Children of the modal
 * @param footer - Footer of the modal
 * @param maxWidth - Maximum width of the modal
 * @param fullWidth - Whether the modal is full width
 * @param showCloseButton - Whether to show the close button
 * @param useGlassCard - Whether to use glass card
 */
interface BaseModalProps extends Omit<DialogProps, "title"> {
  open: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: DialogProps["maxWidth"];
  fullWidth?: boolean;
  showCloseButton?: boolean;
  useGlassCard?: boolean;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(0, 0, 0, 0.7)"
      : "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  "& .MuiDialog-paper": {
    borderRadius: 16,
    overflow: "hidden",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, #F4A25980, transparent)`,
    borderRadius: "2px",
  },
  "& .MuiTypography-root": {
    fontWeight: 700,
    fontSize: "1.25rem",
    background: `linear-gradient(135deg, #F4A259 0%, #E8944A 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "0.3px",
  },
}));

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    color: "#F4A259",
    transform: "rotate(90deg)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(0),
  "&.MuiDialogContent-root": {
    padding: theme.spacing(0),
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  background: theme.palette.mode === "dark"
    ? "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)"
    : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.8) 100%)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  gap: theme.spacing(1.5),
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, transparent, #F4A25980, transparent)`,
    borderRadius: "2px",
  },
  "& .MuiButton-contained": {
    backgroundColor: "#F4A259",
    color: "#ffffff",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#E8944A",
    },
    "&:disabled": {
      backgroundColor: "rgba(244, 162, 89, 0.5)",
      color: "rgba(255, 255, 255, 0.7)",
    },
  },
  "& .MuiButton-outlined": {
    borderColor: "#F4A259",
    color: "#F4A259",
    "&:hover": {
      backgroundColor: "rgba(244, 162, 89, 0.08)",
      borderColor: "#F4A259",
    },
    "&:disabled": {
      borderColor: "rgba(244, 162, 89, 0.5)",
      color: "rgba(244, 162, 89, 0.5)",
    },
  },
}));

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = "sm",
  fullWidth = true,
  showCloseButton = true,
  useGlassCard = true,
  ...dialogProps
}) => {
  const theme = useTheme();

  const content = useGlassCard ? (
    <Card sx={{ m: 0, p: 0, border: "none", borderRadius: 0 }}>
      {children}
    </Card>
  ) : (
    children
  );

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          background: useGlassCard ? "transparent" : theme.palette.mode === "dark"
            ? "rgba(18, 18, 18, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: useGlassCard ? "none" : "blur(20px)",
          WebkitBackdropFilter: useGlassCard ? "none" : "blur(20px)",
          boxShadow: useGlassCard
            ? "none"
            : theme.palette.mode === "dark"
              ? "0 24px 48px rgba(0, 0, 0, 0.5)"
              : "0 24px 48px rgba(0, 0, 0, 0.15)",
        },
      }}
      {...dialogProps}
    >
      <StyledDialogTitle>
        {typeof title === "string" ? (
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        ) : (
          title
        )}
        {showCloseButton && (
          <StyledCloseButton
            aria-label="close"
            onClick={onClose}
          >
            <CloseIcon />
          </StyledCloseButton>
        )}
      </StyledDialogTitle>
      <StyledDialogContent>
        {content}
      </StyledDialogContent>
      {footer && (
        <StyledDialogActions>
          {footer}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

export default BaseModal;
